/**
 * Level 3 — the things that are about the PLAYER, not about the game.
 *
 * ============================ WHAT LIVES HERE ==================================
 * `PRODUCT-FLOW-AND-PRICING.md` §2 gives every control in the product exactly one
 * level. Level 1 is a card, Level 2 is the Custom screen, and Level 3 is this: pace,
 * sounds, taunts, deck colour, reduced motion, and the four advanced dials that decide
 * how a table is dealt rather than what is dealt (hero seat, fast bots, seed, seed
 * lock). Nothing here changes what a hand IS — that is the test for whether something
 * belongs on this list.
 *
 * Two settings are deliberately NOT here, because they already have a store and a
 * second copy of a setting is a setting that disagrees with itself:
 *
 *   - money display  -> `lib/money.ts` (`loadMoneyMode` / `saveMoneyMode`)
 *   - table pace     -> `table/settings.ts` (`getPace` / `setPace` / `onPaceChange`)
 *
 * The Settings sheet reads and writes through those two, exactly as `settings.ts`'s
 * own header said it should.
 *
 * ## And two things that are not settings at all
 *
 * The player's PERSONA (their portrait) and their SAVED CARDS are also kept here, for
 * one reason: they are the same kind of value — client-side, per device, guarded, and
 * gone the day a server owns them. Phase 4 gives the account a wallet and Phase F gives
 * it a roster; both will move, and moving them is one file.
 *
 * ## Every browser touch is guarded
 *
 * Private mode throws on `localStorage`, and the harness renders these components with
 * no storage at all. A preference that cannot be read is the default, never an
 * exception — the same rule `table/settings.ts` already follows.
 * ===============================================================================
 */
import { useEffect, useState } from "react";

/** The card back the player looks at. Both are real art; see `sync-art.mjs`. */
export type DeckColour = "red" | "plum";
/** Phase 7: which projection of the table. See `settings/view.ts` for the default rule. */
export type TableView = "saloon" | "topdown";

export interface TablePrefs {
  /** Deal the next hand on its own. Was a checkbox under the felt on `/play`. */
  autoDeal: boolean;
  /**
   * The sound sprite plays, or it does not. PHASE 6: this is now wired.
   *
   * `table/sounds.ts` reads it at the moment of a click and mutes the master gain.
   * `prefers-reduced-motion` deliberately does NOT feed into it — a player who asked
   * for less movement did not ask for silence.
   */
  sound: boolean;
  /** 0..1, the master gain. Phase 6. */
  soundVolume: number;
  /**
   * Table talk: bubbles, expressions and the hero's tray. PHASE 6: wired.
   *
   * Off hides every bubble AND disables the tray, per table and per device — `voice.ts`
   * reads it on every decision, so turning it off mid-hand takes effect on the next
   * one rather than at the next sitting.
   */
  taunts: boolean;
  deck: DeckColour;
  /**
   * The player asking for less motion even where the operating system does not.
   *
   * The OS setting is read by `prefersReducedMotion()` and is never stored — a player
   * who set it there expects every page to honour it. This is the other direction: an
   * override for somebody whose machine says nothing and whose eyes disagree.
   */
  reducedMotion: boolean;
  /**
   * PHASE 7: the saloon view or the top-down table, remembered on this device.
   *
   * `null` is "never chosen", and it is not the same as either answer: an unchosen
   * device gets the saloon on a wide screen and the top-down table everywhere else
   * (`settings/view.ts`), and a phone never gets the saloon whatever is stored here —
   * the saloon needs the width. A choice made on a desktop is kept for that desktop.
   */
  view: TableView | null;
  /* -------------------------------------------------------------- advanced --- */
  heroSeat: number;
  fastBots: boolean;
  seed: string;
  seedLocked: boolean;
}

export const DEFAULT_PREFS: TablePrefs = {
  autoDeal: true,
  sound: true,
  soundVolume: 0.7,
  taunts: true,
  deck: "red",
  reducedMotion: false,
  view: null,
  heroSeat: 0,
  fastBots: false,
  seed: "",
  seedLocked: false,
};

/** A card the player built on the Custom Table screen and asked to keep. */
export interface SavedCard {
  id: string;
  name: string;
  /** A `Setup`, structurally — typed loosely so this module imports no component. */
  setup: Record<string, unknown>;
}

/**
 * The last verdict this player was shown at hand end.
 *
 * Phase 5, §7: the seat card's one line is about what just happened to THIS player,
 * and the most recent thing that happened is the last hand the model disagreed with
 * them about. Kept on the device, like everything else in this file, because it is a
 * one-line memory of a client-side event and a round trip to store it would be a round
 * trip to store a sentence.
 */
export interface LastVerdict { text: string; drill: string; at: number }

/**
 * What the last sitting looked like, for the Barkeep's one line.
 *
 * ================== WHY THIS IS ON THE DEVICE AND NOT IN THE POST ==============
 * The Phase 4 review's answer to question 7: "a sitting is the right unit until Phase
 * 5's Ledger carries pots; then the Ledger feeds the Barkeep's line." This is that
 * feed, and the two numbers in it — the biggest pot the player LOST, and the street the
 * most EV went on — are both computed on the client from the hand history it already
 * holds.
 *
 * They do not ride in the session POST, and that is deliberate rather than lazy. The
 * Worker is closed this phase, so a field added to the body would be a field the server
 * neither stores nor returns: write-only, unreadable by the one screen that wants it,
 * and a migration's worth of work away from being useful. The Barkeep runs in the
 * browser and reads it here. If a later phase opens `app/src/` and wants these on the
 * account rather than the device, they belong in the `stats` object the post already
 * carries — `biggestLostPot` and `worstSpot` — and the migration comes with them.
 * ===============================================================================
 */
export interface LastSitting {
  at: number;
  /** The Play card, so the Barkeep can name the drill it sharpens. */
  cardId: string | null;
  /**
   * And the table's SHAPE, for the sittings a card cannot name.
   *
   * A Custom table has no card. Without these three, `drillFor` fell back to the
   * six-max cash answer for every one of them — the exact mistake the drill chooser's
   * own note says it exists to prevent, reintroduced one module downstream.
   */
  seats?: number;
  stackBb?: number;
  format?: "cash" | "tournament";
  hands: number;
  /** Signed, in the table's own chips. */
  net: number;
  /** The biggest pot this player was in and did not win. Null when there was none. */
  biggestLostPot: number | null;
  /** Where the most EV went: a street and a short label for it. */
  worstSpot: { street: string; label: string } | null;
}

interface Stored {
  prefs: TablePrefs;
  /** The roster slug the player picked as their face. Null until they choose. */
  persona: string | null;
  saved: SavedCard[];
  /** The last Play card taken, so "Sharpen this" can point at its drill. */
  lastCard: string | null;
  /** Phase 5: the seat card's hook, and the Barkeep's line. */
  lastVerdict: LastVerdict | null;
  lastSitting: LastSitting | null;
}

const KEY = "bb.prefs.v1";

const EMPTY: Stored = {
  prefs: DEFAULT_PREFS, persona: null, saved: [], lastCard: null,
  lastVerdict: null, lastSitting: null,
};

let cache: Stored | null = null;
const listeners = new Set<() => void>();

function read(): Stored {
  try {
    const raw = globalThis.localStorage?.getItem(KEY);
    if (!raw) return EMPTY;
    const parsed = JSON.parse(raw) as Partial<Stored>;
    return {
      /* Field by field, so a stored blob written by an older build cannot delete a
         setting this one needs — it just falls back to the default for that one. */
      prefs: { ...DEFAULT_PREFS, ...(parsed.prefs ?? {}) },
      persona: typeof parsed.persona === "string" ? parsed.persona : null,
      saved: Array.isArray(parsed.saved) ? parsed.saved.filter(isSaved) : [],
      lastCard: typeof parsed.lastCard === "string" ? parsed.lastCard : null,
      /* Both are shapes rather than scalars, so both are checked rather than cast: a
         blob written by a future build must not put an object where a string goes. */
      lastVerdict: isVerdict(parsed.lastVerdict) ? parsed.lastVerdict : null,
      lastSitting: isSitting(parsed.lastSitting) ? parsed.lastSitting : null,
    };
  } catch {
    return EMPTY;
  }
}

function isVerdict(v: unknown): v is LastVerdict {
  const c = v as LastVerdict | null;
  return !!c && typeof c.text === "string" && typeof c.drill === "string" && typeof c.at === "number";
}

function isSitting(v: unknown): v is LastSitting {
  const c = v as LastSitting | null;
  return !!c && typeof c.at === "number" && typeof c.hands === "number" && typeof c.net === "number";
}

function isSaved(v: unknown): v is SavedCard {
  const c = v as SavedCard | null;
  return !!c && typeof c.id === "string" && typeof c.name === "string" && !!c.setup;
}

function all(): Stored {
  if (cache === null) cache = read();
  return cache;
}

function write(next: Stored): void {
  cache = next;
  try { globalThis.localStorage?.setItem(KEY, JSON.stringify(next)); } catch { /* best effort */ }
  for (const l of listeners) l();
}

/** Notified whenever anything in the store changes. Returns the unsubscribe. */
export function onPrefsChange(l: () => void): () => void {
  listeners.add(l);
  return () => { listeners.delete(l); };
}

export function getPrefs(): TablePrefs { return all().prefs; }

export function setPref<K extends keyof TablePrefs>(key: K, value: TablePrefs[K]): void {
  const s = all();
  write({ ...s, prefs: { ...s.prefs, [key]: value } });
}

export function getPersona(): string | null { return all().persona; }
export function setPersona(slug: string | null): void { write({ ...all(), persona: slug }); }

export function getSavedCards(): readonly SavedCard[] { return all().saved; }

export function saveCard(name: string, setup: Record<string, unknown>): SavedCard {
  const card: SavedCard = {
    id: `own_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
    name: name.trim() || "My table",
    setup,
  };
  const s = all();
  write({ ...s, saved: [...s.saved, card] });
  return card;
}

export function removeSavedCard(id: string): void {
  const s = all();
  write({ ...s, saved: s.saved.filter((c) => c.id !== id) });
}

/**
 * The card back the deck is printed with, as a URL.
 *
 * `PlayingCard` asks for this every time it draws a face-down card, so a change in the
 * sheet reaches the felt on the next render — which, at a live table, is the next tick
 * of the Director, and at the latest the next hand.
 *
 * The plum back is real art: `sync-art.mjs` ships it beside the red one at the same
 * 750px tier. A setting that stored a colour nothing printed would be the same lie as
 * a mute switch over silence.
 */
const DECK_BACKS: Record<DeckColour, string> = {
  red: "/art/cards/back@1x.png",
  plum: "/art/cards/back-plum-750.webp",
};

export function deckBackUrl(): string {
  return DECK_BACKS[all().prefs.deck] ?? DECK_BACKS.red;
}

/**
 * Does the player want less motion than their machine asked for?
 *
 * The operating system's own setting is read by `table/settings.ts` and is never
 * stored — a player who set it there expects every page to honour it. This is the
 * other direction, and the two are ORed by whoever computes the timeline.
 */
export function reducedMotionOverride(): boolean {
  return all().prefs.reducedMotion;
}

export function getLastVerdict(): LastVerdict | null { return all().lastVerdict; }
export function setLastVerdict(v: LastVerdict | null): void { write({ ...all(), lastVerdict: v }); }

export function getLastSitting(): LastSitting | null { return all().lastSitting; }
export function setLastSitting(v: LastSitting | null): void { write({ ...all(), lastSitting: v }); }

export function getLastCard(): string | null { return all().lastCard; }
export function setLastCard(id: string): void { write({ ...all(), lastCard: id }); }

/* ------------------------------------------------------------------ hooks --- */

/**
 * The whole store, re-rendering on every change.
 *
 * One hook rather than one per value: the Settings sheet reads eight of these at once,
 * and eight subscriptions to the same store is eight renders for one tap.
 */
export function usePrefsStore(): {
  prefs: TablePrefs;
  persona: string | null;
  saved: readonly SavedCard[];
  lastCard: string | null;
} {
  const [, bump] = useState(0);
  useEffect(() => onPrefsChange(() => bump((n) => n + 1)), []);
  const s = all();
  return { prefs: s.prefs, persona: s.persona, saved: s.saved, lastCard: s.lastCard };
}
